import { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { TrendingUp, Gauge } from "lucide-react";

function AnimatedNumber({ value, start, duration = 1200 }) {
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!start) return;
        let raf;
        let t0;
        const step = (t) => {
            if (!t0) t0 = t;
            const p = Math.min((t - t0) / duration, 1);
            setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [value, start, duration]);
    return <>{n}</>;
}

function MetricBar({ label, unit, serie, opt, inView }) {
    const max = Math.max(opt, serie) * 1.08 || 1;
    return (
        <div data-testid={`metric-${label.toLowerCase()}`}>
            <div className="mb-2 flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.18em] text-[#A1A1AA]">{label}</span>
                <span className="text-xs text-[#52525B]">{unit}</span>
            </div>

            <div className="space-y-2.5">
                <div className="flex items-center gap-3">
                    <span className="w-16 shrink-0 text-xs text-[#A1A1AA]">Serie</span>
                    <div className="relative h-7 flex-1 overflow-hidden rounded-md bg-[#1A1A1A]">
                        <motion.div
                            className="h-full rounded-md bg-[#3F3F46]"
                            initial={{ width: 0 }}
                            animate={inView ? { width: `${(serie / max) * 100}%` } : {}}
                            transition={{ duration: 1, ease: "easeOut" }}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-white">
                            {serie}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <span className="w-16 shrink-0 text-xs font-semibold text-white">Optimiert</span>
                    <div className="relative h-7 flex-1 overflow-hidden rounded-md bg-[#1A1A1A]">
                        <motion.div
                            className="h-full rounded-md bg-gradient-to-r from-[#CC0000] to-[#FF3B30]"
                            initial={{ width: 0 }}
                            animate={inView ? { width: `${(opt / max) * 100}%` } : {}}
                            transition={{ duration: 1.2, ease: "easeOut", delay: 0.15 }}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-white">
                            <AnimatedNumber value={opt} start={inView} />
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export const PowerComparison = ({
    serie_ps,
    serie_nm,
    opt_ps,
    opt_nm,
    stageName = "Stage 1",
    verbrauch,
    beschleunigung,
}) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-60px" });
    const psGain = Math.max(opt_ps - serie_ps, 0);
    const nmGain = Math.max(opt_nm - serie_nm, 0);
    const pct = serie_ps ? Math.round((psGain / serie_ps) * 100) : 0;

    return (
        <div
            ref={ref}
            data-testid="power-comparison"
            className="overflow-hidden rounded-2xl border border-border bg-[#0F0F0F]"
        >
            <div className="flex items-center justify-between border-b border-border bg-surface px-5 py-4">
                <div className="flex items-center gap-2">
                    <Gauge className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold text-white">Leistungsvergleich</span>
                </div>
                <span className="rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
                    {stageName}
                </span>
            </div>

            <div className="space-y-7 p-5 sm:p-6">
                <MetricBar label="Leistung" unit="PS" serie={serie_ps} opt={opt_ps} inView={inView} />
                <MetricBar label="Drehmoment" unit="Nm" serie={serie_nm} opt={opt_nm} inView={inView} />

                <div className="grid grid-cols-3 gap-3 border-t border-border pt-5">
                    <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-2xl font-bold text-success">
                            <TrendingUp className="h-4 w-4" />+<AnimatedNumber value={psGain} start={inView} />
                        </div>
                        <div className="mt-1 text-xs text-[#A1A1AA]">PS Mehrleistung</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-success">
                            +<AnimatedNumber value={nmGain} start={inView} />
                        </div>
                        <div className="mt-1 text-xs text-[#A1A1AA]">Nm Drehmoment</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-primary">
                            +<AnimatedNumber value={pct} start={inView} />%
                        </div>
                        <div className="mt-1 text-xs text-[#A1A1AA]">Steigerung</div>
                    </div>
                </div>

                {(verbrauch || beschleunigung) && (
                    <div className="grid gap-2 sm:grid-cols-2">
                        {beschleunigung && (
                            <div className="rounded-lg border border-border bg-surface px-4 py-3 text-sm">
                                <div className="text-xs text-[#52525B]">Beschleunigung</div>
                                <div className="text-white">{beschleunigung}</div>
                            </div>
                        )}
                        {verbrauch && (
                            <div className="rounded-lg border border-border bg-surface px-4 py-3 text-sm">
                                <div className="text-xs text-[#52525B]">Verbrauch</div>
                                <div className="text-white">{verbrauch}</div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PowerComparison;
